import React, { FC, Fragment, KeyboardEvent, useState } from 'react';
import clsx from 'clsx';
import { useSettings } from '../../../../hooks/useSettings';
import Star from './components/Star';
import { useTranslations } from '../../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickTypes,
  NarvarClickSubTypes,
} from '../../../../hooks/Analytics';
import styles from './StarRating.module.css';
import RatingText from './components/RatingText';

export interface StarRatingProps {
  onClick?: (index: number, selectedText?: string) => void;
  onKeyDown?: (e: KeyboardEvent, index: number, selectedText?: string) => void;
  selectedRating: number;
  featureName?: string;
  isLandscape?: boolean;
  displayTextFirst?: boolean;
}

const StarRating: FC<StarRatingProps> = ({
  onClick,
  onKeyDown,
  selectedRating,
  featureName,
  isLandscape = false,
  displayTextFirst = false,
}) => {
  const { feedback } = useSettings();

  const { enableStarRatingText: enableStarRatingTextIn } = feedback || {};
  const enableStarRatingText =
    typeof enableStarRatingTextIn === 'boolean' ? enableStarRatingTextIn : true;

  const {
    feedbackRatingShortOne,
    feedbackRatingShortTwo,
    feedbackRatingShortThree,
    feedbackRatingShortFour,
    feedbackRatingShortFive,
    feedbackRatingSubcaption,
  } = useTranslations();

  const [hovered, setHovered] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const logEvent = useAnalyticsPost();

  const ratingText = [
    feedbackRatingShortOne || '',
    feedbackRatingShortTwo || '',
    feedbackRatingShortThree || '',
    feedbackRatingShortFour || '',
    feedbackRatingShortFive || '',
  ];

  const handleClick = (submittedRating: number): void => {
    logEvent({
      clickCategory: NarvarClickSubTypes.RATING,
      clickSubType: NarvarClickSubTypes.RATING,
      clickSurveyRating: submittedRating,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_BLOCK_STAR_RATING,
      eventFeature: NarvarEventComponents.NTH_BLOCK_STAR_RATING,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_STAR_RATING,
    });
    if (onClick) {
      onClick(
        submittedRating,
        `${feedbackRatingSubcaption || ''} ${ratingText[
          submittedRating - 1
        ].toLowerCase()}?`,
      );
    }
  };

  const ratingNumber = hovered ? hoverRating : selectedRating;

  const stars = (
    <div className={styles.fieldSetContainer}>
      <fieldset className={styles.styledFieldset}>
        {/* there are always 5 stars for ratings */}
        {[...Array(5)].map((_, index) => (
          <span key={`star-rating-${index + 1}`}>
            <input
              className={styles.visuallyHiddenInput}
              type="radio"
              id={`rating-star-${index + 1}`}
              name="starRating"
              value={index + 1}
              onFocus={(): void => {
                setHovered(true);
                setHoverRating(index + 1);
              }}
              onBlur={(): void => {
                setHovered(false);
                setHoverRating(0);
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (!onKeyDown) return;
                onKeyDown(
                  e,
                  index + 1,
                  `${feedbackRatingSubcaption || ''} ${ratingText[
                    index
                  ].toLowerCase()}?`,
                );
              }}
              onClick={(): void => {
                handleClick(index + 1);
              }}
              aria-label={ratingText[index]}
              data-test={`starRatingIconButton${index + 1}`}
            />
            <label
              className={styles.inputLabel}
              htmlFor={`rating-star-${index + 1}`}
              onMouseEnter={(): void => {
                setHovered(true);
                setHoverRating(index + 1);
              }}
              onMouseLeave={(): void => {
                setHovered(false);
                setHoverRating(0);
              }}
            >
              <Star
                full={
                  hovered
                    ? hoverRating >= index + 1
                    : selectedRating >= index + 1
                }
                s={{
                  width: 'calc(100% - 0.5rem)',
                  margin: '0 auto',
                  height: '2rem',
                }}
                data-test={`starRatingIconStar${index + 1}`}
              />
            </label>
          </span>
        ))}
      </fieldset>
    </div>
  );

  const text = (
    <RatingText
      ratingText={ratingText}
      ratingNumber={ratingNumber}
      selectedRating={selectedRating}
      isLandscape={isLandscape}
      feedbackRatingSubcaption={feedbackRatingSubcaption || ''}
    />
  );

  const starsClasses = clsx({
    [styles.stars]: true,
    [styles.textFirst]: displayTextFirst,
  });
  if (isLandscape) {
    const starsAndText = [
      <div key="stars" className={styles.stars}>
        {stars}
      </div>,
      <Fragment key="text">
        {enableStarRatingText && <div className={starsClasses}>{text}</div>}
      </Fragment>,
    ];
    if (displayTextFirst) starsAndText.reverse();
    return <div>{starsAndText}</div>;
  }

  return (
    <div className={styles.center}>
      {stars}
      {enableStarRatingText && <div className={styles.ratingText}>{text}</div>}
    </div>
  );
};

export default StarRating;
